import {
  entities,
  InvalidTokenError,
  plainToInstance,
  UserAuthenticationError,
  utilities,
} from "@fraction/shared";
import fraction from "src/api/fraction";
import { clearSessionCookie, saveSessionCookie } from "src/auth/sessionCookie";
import { safeLocalStorage } from "src/stores/safeLocalStorage";

const getUser = () => {
  const user = safeLocalStorage.getItem("user");
  if (user) {
    return plainToInstance(entities.User, JSON.parse(user) as object);
  }
};

const setUser = (user: entities.UserT) => safeLocalStorage.setItem("user", JSON.stringify(user));

export const STORAGE = {
  getToken: () => {
    const token = safeLocalStorage.getItem("token");
    if (token) {
      return JSON.parse(token);
    }
  },
  setToken: (token: string) => {
    safeLocalStorage.setItem("token", JSON.stringify(token));
    saveSessionCookie(token);
  },
  clearToken: () => {
    safeLocalStorage.removeItem("token");
    clearSessionCookie();
  },
  getUser,
  setUser,
  setMergeUser: (user: entities.UserT) => {
    const existingUser = getUser();
    if (existingUser && existingUser?.id === user?.id) {
      return setUser({ ...existingUser, ...user });
    }
    return setUser(user);
  },
  clearUser: () => safeLocalStorage.removeItem("user"),
};

export function handleToken(token: string) {
  if (!token) {
    throw new InvalidTokenError("No token provided");
  }
  try {
    const user = utilities.auth.decodeToken(token) as entities.User;
    STORAGE.setToken(token);
    STORAGE.setMergeUser(user);
    fraction.setBearerToken(token);
    return user;
  } catch (_err) {
    throw new InvalidTokenError("Unable to decode");
  }
}

export async function loadUser(token: string) {
  if (!token) {
    return null;
  }

  try {
    const user = await fraction.getUser(token);
    return user;
  } catch (err) {
    if (err instanceof UserAuthenticationError) {
      // failed to fetch user with the provided token so clear from storage
      STORAGE.clearToken();
      STORAGE.clearUser();
    }

    throw err;
  }
}

export async function loginFromToken(token: string) {
  handleToken(token);
  // login endpoint does not return the full User entity, so after successful login, immediately fetch the user.
  const user = await fraction.getUser(token);

  if (!user) {
    throw new UserAuthenticationError("Unable to login");
  }
  return user;
}

export async function login(data: entities.User) {
  const result = await fraction.login(data);
  // Correct password but the account isn't registered yet — surface this so the
  // caller can route into the OTP claim/verify flow instead of logging in.
  if (result.needsVerification) {
    return { needsVerification: true, userId: result.userId };
  }
  if (!result.token) {
    throw new UserAuthenticationError("Unable to login");
  }
  return { user: await loginFromToken(result.token), token: result.token };
}

export async function logout() {
  STORAGE.clearToken();
  fraction.clearBearerToken();
}
